/**
 * Splits a recording into pieces that can be uploaded while recording, and later put together into the cut video
 * without uploading or encoding the video again: Azure builds the video from a list of the uploaded pieces.
 *
 * A WebM file is a header followed by clusters. A cluster starts with a timestamp, and each frame in it stores its time
 * relative to that timestamp. Cutting out a part moves all later frames to an earlier time, so the cut video gets new
 * cluster headers with shifted timestamps. Each piece starts at a video keyframe, so the video can start with any of
 * them. Frames are timed relative to the group of pieces they belong to, so a group needs only one new cluster header.
 */

// Frames store their time relative to the cluster as a signed 16 bit number of milliseconds, so a group can be up to 32 s long
const GROUP_MILLISECONDS = 30_000

const SEGMENT_ID = 0x18538067
const TRACKS_ID = 0x1654ae6b
const TRACK_ENTRY_ID = 0xae
const TRACK_NUMBER_ID = 0xd7
const TRACK_TYPE_ID = 0x83
const VIDEO_TRACK_TYPE = 1
const CLUSTER_ID = 0x1f43b675
const CUES_ID = 0x1c53bb6b
const TIMECODE_ID = 0xe7
const SIMPLE_BLOCK_ID = 0xa3
const BLOCK_GROUP_ID = 0xa0
const BLOCK_ID = 0xa1
const REFERENCE_BLOCK_ID = 0xfb

export type RecordingPiece = {
  // The number of the Azure block that holds the piece. Block 0 holds the file header.
  blockIndex: number
  size: number
  // Milliseconds since the recording started, until the last frame of the piece
  start: number
  end: number
  // The video can only start with a piece that starts with a keyframe. A piece without a keyframe starts when a group is full.
  isCutPoint: boolean
  // The time that the frames of this piece are relative to, which is the end of its group
  groupTimestamp: number
}

export type RecordingBlock = { index: number; data: Uint8Array }

// A part of the recording that ends up in the video, in seconds
export type RecordingSegment = { start: number; end: number }

type ElementHeader = { id: number; size: number | null; length: number }

function readVint(data: Uint8Array, position: number, keepMarker: boolean) {
  if (position >= data.length) {
    return null
  }
  const length = Math.clz32(data[position]) - 23
  if (position + length > data.length) {
    return null
  }
  let value = data[position]
  if (!keepMarker) {
    value &= 0xff >> length
  }
  for (let index = 1; index < length; index++) {
    value = value * 256 + data[position + index]
  }
  // All bits set means that the size is unknown, which Chrome uses for the segment and the clusters
  const isUnknown = !keepMarker && value === 2 ** (7 * length) - 1
  return { value, length, isUnknown }
}

function readElementHeader(data: Uint8Array, position: number): ElementHeader | null {
  const id = readVint(data, position, true)
  const size = id && readVint(data, position + id.length, false)
  if (!id || !size) {
    return null
  }
  return { id: id.value, size: size.isUnknown ? null : size.value, length: id.length + size.length }
}

function readUint(data: Uint8Array, start: number, end: number) {
  let value = 0
  for (let position = start; position < end; position++) {
    value = value * 256 + data[position]
  }
  return value
}

function* children(data: Uint8Array, start: number, end: number) {
  for (let position = start; position < end; ) {
    const header = readElementHeader(data, position)!
    yield { ...header, start: position + header.length, end: position + header.length + header.size! }
    position += header.length + header.size!
  }
}

function findVideoTrackNumber(tracks: Uint8Array, header: ElementHeader) {
  for (const trackEntry of children(tracks, header.length, tracks.length)) {
    if (trackEntry.id !== TRACK_ENTRY_ID) {
      continue
    }
    const fields = [...children(tracks, trackEntry.start, trackEntry.end)]
    const typeField = fields.find((field) => field.id === TRACK_TYPE_ID)!
    if (readUint(tracks, typeField.start, typeField.end) === VIDEO_TRACK_TYPE) {
      const numberField = fields.find((field) => field.id === TRACK_NUMBER_ID)!
      return readUint(tracks, numberField.start, numberField.end)
    }
  }
  throw new Error("The recording has no video track")
}

/**
 * Reads a frame, which is a SimpleBlock or a BlockGroup with a Block: its track, whether it is a keyframe,
 * where its timestamp is stored and the encoded frame itself.
 */
function readFrame(element: Uint8Array, header: ElementHeader) {
  let blockStart = header.length
  let blockEnd = element.length
  let isKeyframe = false
  if (header.id === SIMPLE_BLOCK_ID) {
    const track = readVint(element, blockStart, false)!
    isKeyframe = (element[blockStart + track.length + 2] & 0x80) !== 0
  } else {
    const blockGroupChildren = [...children(element, header.length, element.length)]
    const block = blockGroupChildren.find((child) => child.id === BLOCK_ID)!
    blockStart = block.start
    blockEnd = block.end
    // A frame that references another frame is not a keyframe
    isKeyframe = !blockGroupChildren.some((child) => child.id === REFERENCE_BLOCK_ID)
  }
  const track = readVint(element, blockStart, false)!
  return {
    track: track.value,
    isKeyframe,
    timestampView: new DataView(element.buffer, element.byteOffset + blockStart + track.length, 2),
    // After the track, the timestamp and the flags
    data: element.subarray(blockStart + track.length + 3, blockEnd),
  }
}

export type PieceFrame = ReturnType<typeof readFrame> & { element: Uint8Array; timestamp: number }

/**
 * Returns the frames of an uploaded piece, with their time in milliseconds since the recording started.
 */
export function* readPieceFrames(piece: RecordingPiece, data: Uint8Array): Generator<PieceFrame> {
  for (const child of children(data, 0, data.length)) {
    const element = data.subarray(child.start - child.length, child.end)
    const frame = readFrame(element, child)
    yield { ...frame, element, timestamp: piece.groupTimestamp + frame.timestampView.getInt16(0) }
  }
}

/**
 * Splits the recording that MediaRecorder emits in chunks into the file header and pieces.
 */
export class RecordingSplitter {
  pieces: RecordingPiece[] = []
  videoTrackNumber = 0
  private buffer = new Uint8Array(0)
  private hasClusters = false
  private headerParts: Uint8Array[] = []
  private clusterTimestamp = 0
  private groupStart = 0
  private currentPiece: RecordingPiece | null = null
  private currentPieceParts: Uint8Array[] = []

  /**
   * Returns the blocks that this chunk of the recording completed.
   */
  push(chunk: Uint8Array): RecordingBlock[] {
    const buffer = new Uint8Array(this.buffer.length + chunk.length)
    buffer.set(this.buffer)
    buffer.set(chunk, this.buffer.length)
    const blocks: RecordingBlock[] = []
    let position = 0
    for (let header = readElementHeader(buffer, position); header; header = readElementHeader(buffer, position)) {
      // The segment and the clusters have an unknown size, so only their header is read and their children follow
      if (header.id === SEGMENT_ID) {
        this.headerParts.push(buffer.slice(position, position + header.length))
        position += header.length
        continue
      }
      if (header.id === CLUSTER_ID) {
        // Everything before the first cluster is the file header
        if (!this.hasClusters) {
          blocks.push({ index: 0, data: concat(this.headerParts) })
          this.hasClusters = true
        }
        position += header.length
        continue
      }
      const end = position + header.length + header.size!
      if (end > buffer.length) {
        break
      }
      const element = buffer.slice(position, end)
      position = end
      if (!this.hasClusters) {
        if (header.id === TRACKS_ID) {
          this.videoTrackNumber = findVideoTrackNumber(element, header)
        }
        this.headerParts.push(element)
      } else if (header.id === TIMECODE_ID) {
        this.clusterTimestamp = readUint(element, header.length, element.length)
      } else if (header.id === SIMPLE_BLOCK_ID || header.id === BLOCK_GROUP_ID) {
        blocks.push(...this.addFrame(element, header))
      } else if (header.id === CUES_ID) {
        // Safari ends the recording with an index of the clusters, which the cut video does not use
      } else {
        throw new Error(`Unexpected element ${header.id.toString(16)} in a cluster`)
      }
    }
    this.buffer = buffer.slice(position)
    return blocks
  }

  /**
   * Returns the last piece once the recording has stopped.
   */
  finish(): RecordingBlock[] {
    if (this.buffer.length) {
      throw new Error("The recording ended within an element")
    }
    return this.finishPiece()
  }

  private addFrame(element: Uint8Array, header: ElementHeader): RecordingBlock[] {
    const { track, isKeyframe, timestampView } = readFrame(element, header)
    const timestamp = this.clusterTimestamp + timestampView.getInt16(0)

    const completedBlocks: RecordingBlock[] = []
    const isVideoKeyframe = track === this.videoTrackNumber && isKeyframe
    const isGroupFull = timestamp - this.groupStart > GROUP_MILLISECONDS
    if (!this.currentPiece || isVideoKeyframe || isGroupFull) {
      completedBlocks.push(...this.finishPiece())
      if (!this.currentPiece || isGroupFull) {
        this.groupStart = timestamp
      }
      this.currentPiece = {
        blockIndex: this.pieces.length + 1,
        size: 0,
        start: timestamp,
        end: timestamp,
        // The recording can always start with its first piece
        isCutPoint: isVideoKeyframe || !this.pieces.length,
        groupTimestamp: this.groupStart + GROUP_MILLISECONDS,
      }
    }
    timestampView.setInt16(0, timestamp - this.currentPiece.groupTimestamp)
    this.currentPiece.end = timestamp
    this.currentPieceParts.push(element)
    return completedBlocks
  }

  private finishPiece(): RecordingBlock[] {
    if (!this.currentPieceParts.length) {
      return []
    }
    const data = concat(this.currentPieceParts)
    const piece = { ...this.currentPiece!, size: data.length }
    this.pieces.push(piece)
    this.currentPieceParts = []
    return [{ index: piece.blockIndex, data }]
  }
}

export function concat(parts: Uint8Array[]) {
  const result = new Uint8Array(parts.reduce((size, part) => size + part.length, 0))
  let position = 0
  for (const part of parts) {
    result.set(part, position)
    position += part.length
  }
  return result
}

function clusterHeader(timestamp: number, contentSize: number) {
  const header = new Uint8Array(22)
  const view = new DataView(header.buffer)
  view.setUint32(0, CLUSTER_ID)
  // An 8 byte size: the length marker in the first byte, followed by the size in 7 bytes
  view.setBigUint64(4, (1n << 56n) | BigInt(10 + contentSize))
  view.setUint8(12, TIMECODE_ID)
  view.setUint8(13, 0x88)
  view.setBigUint64(14, BigInt(timestamp))
  return header
}

/**
 * Returns the blocks that make up the video with only the given segments (in seconds) of the recording:
 * the new cluster headers to upload, and the order of all blocks.
 * Segments can come with their own pieces, when cuts between keyframes were re-encoded, see reencodedCuts.ts.
 */
export function assembleRecording(pieces: RecordingPiece[], segments: RecordingSegment[], segmentPieces?: RecordingPiece[][]) {
  const headers: RecordingBlock[] = []
  const blockIndexes = [0]
  let outputTime = 0
  for (const [segmentIndex, segment] of segments.entries()) {
    const start = Math.round(segment.start * 1000)
    const end = Math.round(segment.end * 1000)
    // The segment starts at a cut point, so its first piece starts exactly at the segment's start
    const shift = outputTime - start
    const keptPieces = segmentPieces?.[segmentIndex] ?? pieces.filter((piece) => piece.start >= start && piece.start < end)
    // Map.groupBy would be shorter, but Chrome only has it since version 117, while older versions can record too
    const clusters: RecordingPiece[][] = []
    for (const piece of keptPieces) {
      const cluster = clusters.at(-1)
      if (cluster && cluster[0].groupTimestamp === piece.groupTimestamp) {
        cluster.push(piece)
      } else {
        clusters.push([piece])
      }
    }
    for (const clusterPieces of clusters) {
      const contentSize = clusterPieces.reduce((size, piece) => size + piece.size, 0)
      const header = { index: pieces.length + 1 + headers.length, data: clusterHeader(clusterPieces[0].groupTimestamp + shift, contentSize) }
      headers.push(header)
      blockIndexes.push(header.index, ...clusterPieces.map((piece) => piece.blockIndex))
    }
    outputTime += end - start
  }
  return { headers, blockIndexes }
}
